/**
 * UranIA PA — API client tenant LLM providers (admin only).
 *
 * Wrapper su:
 *   - ``GET    /api/v1/admin/llm-providers``           (admin)
 *   - ``POST   /api/v1/admin/llm-providers``           (create)
 *   - ``PUT    /api/v1/admin/llm-providers/{id}``      (update)
 *   - ``DELETE /api/v1/admin/llm-providers/{id}``      (delete)
 *   - ``POST   /api/v1/admin/llm-providers/{id}/test`` (ping connettivita')
 *   - ``GET    /api/v1/llm-providers/defaults``        (base_url+model di default)
 *
 * La api_key NON e' mai esposta in chiaro nelle GET admin: il backend
 * ritorna solo ``api_key_hint`` (es. ``****abcd``).
 */

import { useAuthStore } from '../auth/auth-store.ts';
import { loadSpaEnv } from '../env.ts';

export type LlmProviderType = 'anthropic' | 'openai' | 'grok' | 'lmstudio';
export type LlmProviderScope = 'core' | 'tenant';

export interface LlmProviderAdminDto {
  readonly id: string;
  readonly tenant_id: string | null;
  readonly provider_type: LlmProviderType;
  readonly label: string;
  readonly model: string;
  readonly base_url: string;
  readonly api_key_hint: string;
  readonly is_active: boolean;
  readonly is_primary: boolean;
  readonly priority: number;
  readonly timeout_seconds: number;
  readonly max_tokens: number;
  readonly temperature: number;
  readonly created_at: string;
  readonly updated_at: string | null;
}

export interface ListLlmProvidersDto {
  readonly items: readonly LlmProviderAdminDto[];
  readonly total: number;
}

export interface UpsertLlmProviderInput {
  readonly provider_type: LlmProviderType;
  readonly label: string;
  readonly model: string;
  readonly base_url: string;
  readonly api_key?: string | null;
  readonly scope: LlmProviderScope;
  readonly is_active: boolean;
  readonly is_primary: boolean;
  readonly priority: number;
  readonly timeout_seconds: number;
  readonly max_tokens: number;
  readonly temperature: number;
}

export interface TestConnectionResult {
  readonly ok: boolean;
  readonly status_code: number | null;
  readonly detail: string;
  readonly models_count: number | null;
}

export interface ProviderDefaults {
  readonly base_url: string;
  readonly model: string;
}

export type ProviderDefaultsMap = Readonly<Record<LlmProviderType, ProviderDefaults>>;

export class LlmProvidersApiError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  constructor(status: number, title: string, detail: string | null) {
    super(title);
    this.name = 'LlmProvidersApiError';
    this.status = status;
    this.detail = detail;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

async function parseProblem(response: Response): Promise<LlmProvidersApiError> {
  let title = response.statusText || 'http_error';
  let detail: string | null = null;
  try {
    const body = (await response.json()) as { title?: unknown; detail?: unknown };
    if (typeof body.title === 'string' && body.title.length > 0) title = body.title;
    if (typeof body.detail === 'string') detail = body.detail;
  } catch {
    // no problem+json
  }
  return new LlmProvidersApiError(response.status, title, detail);
}

async function requestJson<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
  const response = await fetch(buildUrl(path), init);
  if (!response.ok) {
    throw await parseProblem(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function listLlmProviders(): Promise<ListLlmProvidersDto> {
  return requestJson<ListLlmProvidersDto>('GET', '/api/v1/admin/llm-providers');
}

export async function createLlmProvider(
  input: UpsertLlmProviderInput,
): Promise<LlmProviderAdminDto> {
  return requestJson<LlmProviderAdminDto>(
    'POST',
    '/api/v1/admin/llm-providers',
    input,
  );
}

export async function updateLlmProvider(
  id: string,
  input: UpsertLlmProviderInput,
): Promise<LlmProviderAdminDto> {
  return requestJson<LlmProviderAdminDto>(
    'PUT',
    `/api/v1/admin/llm-providers/${encodeURIComponent(id)}`,
    input,
  );
}

export async function deleteLlmProvider(id: string): Promise<void> {
  await requestJson<void>(
    'DELETE',
    `/api/v1/admin/llm-providers/${encodeURIComponent(id)}`,
  );
}

export async function testLlmProvider(id: string): Promise<TestConnectionResult> {
  return requestJson<TestConnectionResult>(
    'POST',
    `/api/v1/admin/llm-providers/${encodeURIComponent(id)}/test`,
  );
}

export async function fetchProviderDefaults(): Promise<ProviderDefaultsMap> {
  return requestJson<ProviderDefaultsMap>(
    'GET',
    '/api/v1/llm-providers/defaults',
  );
}
